import { useEffect, useRef } from "react";
import SearchInput from "@/components/SearchInput";
import { useSearchWithParams } from "@/hooks/useSearchParams";

type PeopleSearchInputProps = {
  onDebouncedChange: (value: string) => void;
  clearSignal?: number;
};

/**
 * Isolated search input for the People table. Keeps keystroke state local so
 * typing does not re-render the table/query hook on every character.
 */
export default function PeopleSearchInput({ onDebouncedChange, clearSignal = 0 }: PeopleSearchInputProps) {
  const { searchValue, debouncedSearch, setSearchValue, clearSearch } = useSearchWithParams({
    paramName: "search"
  });

  const onDebouncedChangeRef = useRef(onDebouncedChange);
  onDebouncedChangeRef.current = onDebouncedChange;

  useEffect(() => {
    onDebouncedChangeRef.current(debouncedSearch ?? "");
  }, [debouncedSearch]);

  const prevClearSignalRef = useRef(clearSignal);
  useEffect(() => {
    if (clearSignal !== prevClearSignalRef.current) {
      prevClearSignalRef.current = clearSignal;
      clearSearch();
    }
  }, [clearSignal, clearSearch]);

  return (
    <SearchInput placeholder="Search People" query={searchValue} onChange={setSearchValue} onClear={clearSearch} />
  );
}
